import { useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import DashboardLayout from "layouts/dashboard/DashboardLayout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useDashboardHome } from "hooks/useDashboardHome";
import {
  type MarkTrackEntry,
  type MarkTrackGroup,
  type TrackRecordResult,
} from "api/dashboard";
import { ActionCard, StatCard } from "views/dashboard/dashboardHomeShared";
import { TrackRecordSearchInput } from "views/dashboard/TrackRecordSearchInput";
import { getPathForLeadByModule } from "utils/pathForLeadByModule";
import { resolveStatusMarkPath } from "utils/statusMarkRouting";

import statTrend from "assets/svg/dashboard-stat-trend.svg";
import statDocument from "assets/svg/dashboard-stat-document.svg";
import statChart from "assets/svg/dashboard-stat-chart.svg";
import actionTrackSearch from "assets/svg/dashboard-action-track-search.svg";
import actionPending from "assets/svg/dashboard-action-pending.svg";
import actionReports from "assets/svg/dashboard-action-reports.svg";
import actionUpdate from "assets/svg/dashboard-action-update.svg";

export interface TrackRecordLocationState {
  query: string;
  result: TrackRecordResult;
}

function flattenEntries(groups: MarkTrackGroup[]): MarkTrackEntry[] {
  return groups.flatMap((g) => g.entries);
}

/**
 * dd-mm-yy (zero-padded day/month, 2-digit year). Same for Appl. date and Status updated.
 * Date-only API values (yyyy-MM-dd) are parsed as calendar dates to avoid UTC shift bugs.
 */
function formatDdMmYy(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return "—";
  const trimmed = value.trim();
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  let y: number;
  let m: number;
  let day: number;
  if (dateOnly) {
    y = Number(dateOnly[1]);
    m = Number(dateOnly[2]);
    day = Number(dateOnly[3]);
  } else {
    const d = new Date(trimmed);
    if (Number.isNaN(d.getTime())) return "—";
    y = d.getFullYear();
    m = d.getMonth() + 1;
    day = d.getDate();
  }
  const dd = String(day).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const yy = String(y % 100).padStart(2, "0");
  return `${dd}-${mm}-${yy}`;
}

const thClass =
  "px-3 py-2.5 text-left text-xs font-medium text-[#6d6d6d] border-b border-[#eee] bg-[#f8f8f8] whitespace-nowrap";
const tdClass =
  "px-3 py-2 align-top text-xs leading-5 text-[#222] border-b border-[#f0f0f0]";

function TrackRecordResultsTable({
  rows,
  openingLeadId,
  onOpenRow,
}: {
  rows: MarkTrackEntry[];
  openingLeadId: string | null;
  onOpenRow: (entry: MarkTrackEntry) => void;
}): JSX.Element {
  return (
    <div className="w-full overflow-x-auto rounded-md border border-[#eee] bg-white shadow-sm">
      <table className="w-full min-w-[1040px] border-collapse text-left">
        <thead>
          <tr>
            <th className={`${thClass} min-w-[140px]`}>Module</th>
            <th className={thClass}>Mark</th>
            <th className={thClass}>Type</th>
            <th className={`${thClass} w-[72px]`}>Class</th>
            <th className={thClass}>TM number</th>
            <th className={thClass}>Lead no.</th>
            <th className={`${thClass} whitespace-nowrap`}>Appl. date</th>
            <th className={`${thClass} whitespace-nowrap`}>Status updated</th>
            <th className={`${thClass} min-w-[140px] max-w-[220px]`}>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((entry) => {
            const canOpen = Boolean(entry.leadId?.trim());
            const isOpening = openingLeadId === entry.leadId;
            const disabled = !canOpen || isOpening;
            const rowClass = [
              "transition-colors",
              disabled ? "opacity-60" : "",
              canOpen && !isOpening
                ? "cursor-pointer hover:bg-[#fafafa] focus-visible:bg-[#fafafa] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#222]/30"
                : "",
            ].join(" ");

            const handleActivate = (): void => {
              if (!disabled && entry.leadId) onOpenRow(entry);
            };

            const handleKeyDown = (
              e: React.KeyboardEvent<HTMLTableRowElement>
            ): void => {
              if (!canOpen || isOpening) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleActivate();
              }
            };

            return (
              <tr
                key={`${entry.leadId}-${entry.applicationId ?? entry.tmNo}`}
                tabIndex={canOpen && !isOpening ? 0 : undefined}
                role={canOpen ? "button" : undefined}
                aria-label={
                  canOpen ? `Open application for ${entry.mark}` : undefined
                }
                aria-busy={isOpening || undefined}
                className={rowClass}
                onClick={() => void handleActivate()}
                onKeyDown={handleKeyDown}
              >
                <td className={`${tdClass} max-w-[160px]`}>
                  <span className="break-words">
                    {entry.moduleLabel || entry.module || "—"}
                  </span>
                </td>
                <td className={`${tdClass} max-w-[140px]`}>
                  <span className="break-words">{entry.mark || "—"}</span>
                </td>
                <td className={`${tdClass} max-w-[120px]`}>
                  <span className="break-words">{entry.typeOfMark || "—"}</span>
                </td>
                <td className={`${tdClass} tabular-nums`}>
                  {entry.classNumber ?? "—"}
                </td>
                <td className={`${tdClass} max-w-[120px]`}>
                  <span className="break-all">{entry.tmNo || "—"}</span>
                </td>
                <td className={`${tdClass} max-w-[120px]`}>
                  <span className="break-all">
                    {entry.leadNo?.trim() || "—"}
                  </span>
                </td>
                <td
                  className={`${tdClass} whitespace-nowrap text-[11px] tabular-nums`}
                >
                  {formatDdMmYy(entry.applDate)}
                </td>
                <td
                  className={`${tdClass} whitespace-nowrap text-[11px] tabular-nums`}
                >
                  {formatDdMmYy(entry.statusUpdatedAt)}
                </td>
                <td className={`${tdClass} max-w-[220px]`}>
                  <span className="break-words">
                    {entry.status || "Pending"}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export const TrackRecordResultsView = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as TrackRecordLocationState | undefined;
  const [openingLeadId, setOpeningLeadId] = useState<string | null>(null);
  const [tableSearch, setTableSearch] = useState<string>("");
  const [moduleFilter, setModuleFilter] = useState<string>("");

  const hasValidPayload =
    state != null &&
    typeof state.query === "string" &&
    typeof state.result === "object" &&
    state.result != null &&
    Array.isArray(state.result.groups);

  const [searchValue, setSearchValue] = useState<string>(() =>
    hasValidPayload && state ? state.query : ""
  );

  const {
    stats,
    statsLoading,
    statsError,
    trackResult,
    trackLoading,
    trackError,
    handleTrackRecord,
  } = useDashboardHome({
    initialTrackResult: hasValidPayload && state ? state.result : undefined,
  });

  const rows = flattenEntries(trackResult?.groups ?? []);

  const moduleOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const entry of rows) {
      if (!seen.has(entry.module)) {
        seen.set(entry.module, entry.moduleLabel || entry.module);
      }
    }
    return Array.from(seen.entries()).map(([module, label]) => ({
      module,
      label,
    }));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const term = tableSearch.trim().toLowerCase();
    return rows.filter((entry) => {
      if (moduleFilter && entry.module !== moduleFilter) return false;
      if (!term) return true;
      const haystack = [
        entry.mark,
        entry.typeOfMark,
        entry.classNumber != null ? String(entry.classNumber) : "",
        entry.tmNo,
        entry.leadNo ?? "",
        entry.status ?? "",
        entry.moduleLabel,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [rows, tableSearch, moduleFilter]);

  if (!hasValidPayload) {
    return <Navigate to="/dashboard/home" replace />;
  }

  const totalApplications = stats?.totalApplications ?? 0;
  const activeApplications = stats?.activeApplications ?? 0;
  const discussion = stats?.discussion ?? 0;
  const filing = stats?.filing ?? 0;
  const registeredTms = stats?.registeredTms ?? 0;
  const pendingItemsBadge = discussion + filing;

  const openApplicationForRow = async (entry: MarkTrackEntry): Promise<void> => {
    const id = entry.leadId?.trim();
    if (!id) return;
    setOpeningLeadId(id);
    try {
      // NEW_APP rows in Track results come from filed/acknowledged applications and carry an
      // application status (e.g. "Formalities Check Fail"). The generic last-completed-step
      // resolver maps S3_ steps to status/overview, so for those rows we dispatch through the
      // same status-aware util the pending-items chip click uses — landing on the specific
      // sub-page (with the FC-fail substep where applicable).
      const moduleKey = entry.module.trim().toUpperCase();
      const status = entry.status?.trim();
      const path =
        moduleKey === "NEW_APP" && status
          ? await resolveStatusMarkPath(id, {
              applicationId: entry.applicationId ?? "",
              tmNo: entry.tmNo,
              mark: entry.mark,
              typeOfMark: entry.typeOfMark || null,
              classNumber: entry.classNumber || null,
              status,
            })
          : await getPathForLeadByModule(entry.module, id);
      navigate(path);
    } finally {
      setOpeningLeadId(null);
    }
  };

  const handleSubmitTrack = async (): Promise<void> => {
    const next = await handleTrackRecord(searchValue);
    if (next !== null) {
      navigate("/dashboard/home/track-results", {
        replace: true,
        state: { query: searchValue.trim(), result: next },
      });
    }
  };

  return (
    <DashboardLayout headerBrand="UpRight" headerTitle="Dashboard">
      <main className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-5 md:px-6">
        <div className="mx-auto min-h-0 w-full max-w-[1404px] rounded-lg bg-white p-6">
          <div className="flex w-full flex-col items-start gap-[22px]">
            <div className="flex w-full shrink-0 flex-col items-start gap-1">
              <h1 className="whitespace-nowrap text-2xl font-bold leading-9 text-[#333]">
                Trademark Management Suite
              </h1>
              <p className="max-w-[341px] text-sm font-normal leading-5 text-[#777]">
                Enterprise-grade trademark processing platform
              </p>
            </div>

            {statsError && (
              <p className="text-xs text-[#fb2c36]">{statsError}</p>
            )}

            <div className="flex w-full flex-col items-stretch gap-5 xl:flex-row">
              <StatCard
                title="Total Applications"
                value={statsLoading ? "—" : totalApplications}
                subtitle="+3 this month"
                icon={statTrend}
                iconAlt="Trend"
              />
              <StatCard
                title="Active Applications"
                value={statsLoading ? "—" : activeApplications}
                subtitle="+3 this month"
                icon={statTrend}
                iconAlt="Trend"
              />
              <StatCard
                title="Discussion"
                value={statsLoading ? "—" : discussion}
                subtitle="Awaiting action"
                icon={statDocument}
                iconAlt="Discussion"
              />
              <StatCard
                title="Filling"
                value={statsLoading ? "—" : filing}
                subtitle="Awaiting action"
                icon={statDocument}
                iconAlt="Filling"
              />
              <StatCard
                title="Registered TMs"
                value={statsLoading ? "—" : registeredTms}
                subtitle="Total portfolio"
                icon={statChart}
                iconAlt="Registered"
              />
            </div>

            <div className="flex w-full flex-col items-stretch gap-5 lg:flex-row">
              <ActionCard
                icon={actionTrackSearch}
                iconAlt="Track status"
                title="Track Status"
                subtitle="Monitor Application progress"
                variant="dark"
              />
              <ActionCard
                icon={actionPending}
                iconAlt="Pending items"
                title="Pending Items"
                subtitle="Review pending actions"
                variant="light"
                badge={statsLoading ? undefined : pendingItemsBadge}
                onClick={() => navigate("/dashboard/home/pending-items")}
              />
              <ActionCard
                icon={actionReports}
                iconAlt="Generate reports"
                title="Generate Reports"
                subtitle="Analytics and insights"
                variant="light"
              />
              <ActionCard
                icon={actionUpdate}
                iconAlt="Update status"
                title="Update Status"
                subtitle="Update Status"
                variant="light"
                onClick={() => navigate("/dashboard/home/update-status")}
              />
            </div>

            <TrackRecordSearchInput
              inputId="track-results-input"
              value={searchValue}
              onChange={setSearchValue}
              onSubmit={() => void handleSubmitTrack()}
            />

            <div>
              <button
                type="button"
                onClick={() => void handleSubmitTrack()}
                disabled={trackLoading || !searchValue.trim()}
                className="inline-flex h-12 min-h-12 w-full items-center justify-center rounded-lg bg-[#222] px-4 text-center text-sm font-medium leading-5 text-white transition-colors hover:bg-[#333] disabled:cursor-not-allowed disabled:opacity-50 sm:w-[251px] sm:px-6 sm:text-base sm:leading-6"
              >
                {trackLoading ? "Searching…" : "Track Record"}
              </button>
            </div>

            {trackError && (
              <p className="text-xs text-[#fb2c36]">{trackError}</p>
            )}

            <div className="w-full">
              {rows.length === 0 ? (
                <p className="text-xs leading-5 text-[#62748e]">
                  No records found.
                </p>
              ) : (
                <div className="flex w-full flex-col gap-3">
                  <div className="flex w-full flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                    <div className="flex flex-1 items-center rounded-md border border-[#eee] bg-white px-[10px] py-2">
                      <input
                        type="text"
                        className="bg-transparent w-full flex-1 text-xs font-normal leading-5 text-[#222] outline-none placeholder:text-[#6d6d6d]"
                        value={tableSearch}
                        onChange={(e) => setTableSearch(e.target.value)}
                        placeholder="Search results (mark, type, class, TM no., lead no., status)"
                        aria-label="Search results"
                      />
                    </div>
                    <Select
                      value={moduleFilter || "__ALL__"}
                      onValueChange={(v) =>
                        setModuleFilter(v === "__ALL__" ? "" : v)
                      }
                    >
                      <SelectTrigger
                        aria-label="Filter by module"
                        className="h-[38px] w-full text-xs font-normal leading-5 sm:w-[200px]"
                      >
                        <SelectValue placeholder="All modules" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__ALL__">All modules</SelectItem>
                        {moduleOptions.map((opt) => (
                          <SelectItem key={opt.module} value={opt.module}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {filteredRows.length === 0 ? (
                    <p className="text-xs leading-5 text-[#62748e]">
                      No results match your filters.
                    </p>
                  ) : (
                    <TrackRecordResultsTable
                      rows={filteredRows}
                      openingLeadId={openingLeadId}
                      onOpenRow={(entry) => void openApplicationForRow(entry)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </DashboardLayout>
  );
};
